import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { RouteStore } from 'bernie-core';

import { AlternateDatesModel, HotelViewModel } from 'models';

import { UitkBadge, UitkBadgeTheme } from 'uitk-react-badge';
import { UitkCard, UitkCardContent, UitkCardImageLayout, UitkCardMedia } from 'uitk-react-cards';

import { AdditionalInfo } from './additional-info';
import { AlternateDates } from './alternate-dates';
import { CardLink } from './card-link';
import { HighightedPropertyDetails } from './highlighted-property-details';
import { ListingCommonSection } from './listing-common-section';
import { ListingErrorBoundary } from './listing-error-boundary';
import { ListingFooter } from './footer';
import { ListingName } from './listing-name';
import { ListingTravelAd } from './listing-travel-ad';
import { Neighborhood } from './neighborhood';
import { PlaceholderCard } from './placeholder-card';
import { PriceAndSupportingMessagesContainer } from './price-and-supporting-messages-container';
import { Stars } from './star-rating';
import { TitleHeading } from './title-heading';
import { VipAndPropertyTypeBadge } from './vip-and-property-badge';

interface HorizontalListingProps {
  className: string;
  index?: number;
  loading?: boolean;
  model: HotelViewModel;
  routeStore?: RouteStore;
  isDatelessPdp?: boolean;
  defaultListingTopMarginClass?: string;
  lowerRight?: string;
  stars?: number;
  alternateDatesProps?: {
    handleClick: () => void;
    model: AlternateDatesModel[];
  };
  getTitleComponent: (imageAspectRatio?: string, extraProps?: any) => any;
  imageAspectRatio?: string;
  alternateDatesInfo: {
    collapsedLinkName: string;
    collapsedMsgKey: string;
    collapsedRfrr: string;
    expandedLinkName: string;
    expandedMsgKey: string;
    expandedRfrr: string;
    headerTextKey: string;
  };
  dataStid?: string;
  cardLinkProps?: any;
  routeStoreGoBack?: () => void;
  listingClickHandler?: () => void;
  setItemRef?: () => void;
  mapListingView?: boolean;
}

export const HorizontalListing: React.FunctionComponent<HorizontalListingProps> = ({
  alternateDatesProps,
  index,
  loading,
  model,
  routeStore,
  isDatelessPdp,
  className,
  lowerRight,
  stars,
  getTitleComponent,
  imageAspectRatio,
  alternateDatesInfo,
  dataStid,
  cardLinkProps,
  routeStoreGoBack,
  listingClickHandler,
  setItemRef,
  mapListingView
}) => {
  const {
    alternateDates,
    listingFooter,
    name,
    neighborhood,
    pinnedDetails,
    price,
    propertyListingBadgeModel,
    propertySummary,
    secondaryCardMessage,
    starRatingIcon,
    supportingMessages,
    thumbnail,
    travelAd,
    vipMessage,
    propertyTypeBadge,
    recentlyViewed
  } = model;

  const listingWrapperProps = {
    className,
    'data-stid': dataStid || '',
    ref: setItemRef,
    tabIndex: -1
  };

  const vipAndPropertyTypeBadgeProps = {
    vipMessage,
    propertyTypeBadge,
    recentlyViewed,
    isDatelessPdp
  };

  const listingNameProps = {
    name,
    ariaHidden: true,
    recentlyViewed
  };

  const highlightedPropertyDetailsProps = {
    propertyDetails: propertySummary
  };

  const secondary = propertyListingBadgeModel && propertyListingBadgeModel.secondary;
  const tertiary = propertyListingBadgeModel && propertyListingBadgeModel.tertiary;

  const horizontalPriceAndSupportingMessagesContainerProps = {
    ...{ secondaryCardMessage, supportingMessages, price, secondary },
    viewSmall: false,
    wrapPrice: false,
    priceFontWeight: '600',
    hotelId: model.hotelId,
    recentlyViewed,
    isDatelessPdp
  };

  if (loading) {
    return (
      <li {...listingWrapperProps}>
        <PlaceholderCard index={index} className="horizontal-placeholder" />
      </li>
    );
  }

  const renderListingData = (lineCount?: number) => (
    <>
      <div className="uitk-grid all-cell-align-top">
        <div className="uitk-cell all-cell-fill uitk-type-300">
          <VipAndPropertyTypeBadge {...vipAndPropertyTypeBadgeProps} />
          <ListingName {...listingNameProps} lineCount={lineCount} />
        </div>
        <div className="uitk-grid">
          <div className="uitk-cell all-cell-2-3 uitk-type-300">
            <Stars stars={stars} starRatingIcon={starRatingIcon} />
            <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
            <Neighborhood neighborhood={neighborhood} />
          </div>
        </div>
      </div>
      <div className="uitk-cell all-cell-align-bottom">
        <div className="uitk-grid">
          <div className="uitk-cell all-cell-fill all-cell-align-bottom">
            <ListingTravelAd travelAd={travelAd} />
            <ListingCommonSection model={model} routeStore={routeStore} routeStoreGoBack={routeStoreGoBack} />
          </div>
          <div className="uitk-cell all-cell-shrink all-x-gutter-two all-cell-align-bottom uitk-type-right">
            <PriceAndSupportingMessagesContainer {...horizontalPriceAndSupportingMessagesContainerProps} />
            {tertiary &&
              tertiary.text && (
                <div className="uitk-cell all-x-gutter-two uitk-type-right all-cell-shrink all-t-padding-half">
                  {!isDatelessPdp && <UitkBadge theme={UitkBadgeTheme[tertiary.theme]} text={tertiary.text} />}
                </div>
              )}
            {lowerRight && !isDatelessPdp && <div data-stid="lower-right">{lowerRight}</div>}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <li {...listingWrapperProps}>
      <UitkCard imageLayout={UitkCardImageLayout.LEFT_FULLBLEED}>
        <ListingName name={name} visuallyHidden={true} />
        <div className="link-container uitk-grid">
          {!mapListingView &&
            pinnedDetails &&
            pinnedDetails.heading && <TitleHeading data-stid="title-heading" headingInfo={pinnedDetails} />}
          <UitkCardMedia>
            <ListingErrorBoundary thumbnail={thumbnail} name={name}>
              {getTitleComponent(imageAspectRatio, {
                displaySecondaryBadge: false,
                displayHeading: false
              })}
            </ListingErrorBoundary>
          </UitkCardMedia>
          <UitkCardContent className="all-y-padding-three all-x-padding-three listing-content">
            <AbacusExperiment name="SRP_propertyName_SingleCol">
              <AbacusControl>
                <>
                  <div className="uitk-cell all-cell-fill uitk-type-300">
                    <VipAndPropertyTypeBadge {...vipAndPropertyTypeBadgeProps} />
                    <ListingName {...listingNameProps} />
                    <Stars stars={stars} starRatingIcon={starRatingIcon} />
                    <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                    <Neighborhood neighborhood={neighborhood} />
                    <ListingTravelAd travelAd={travelAd} />
                  </div>
                  <PriceAndSupportingMessagesContainer {...horizontalPriceAndSupportingMessagesContainerProps} />
                  <div className="uitk-cell all-cell-align-bottom">
                    <div className="uitk-grid">
                      <div className="uitk-cell all-cell-fill all-cell-align-bottom">
                        <ListingCommonSection model={model} routeStore={routeStore} routeStoreGoBack={routeStoreGoBack} />
                      </div>
                      <div className="uitk-cell all-cell-shrink all-cell-align-bottom uitk-type-right">
                        {tertiary &&
                          tertiary.text && (
                            <div className="uitk-cell all-x-gutter-two uitk-type-right all-cell-shrink">
                              {!isDatelessPdp && <UitkBadge theme={UitkBadgeTheme[tertiary.theme]} text={tertiary.text} />}
                            </div>
                          )}
                        {lowerRight && !isDatelessPdp && <div data-stid="lower-right">{lowerRight}</div>}
                      </div>
                    </div>
                  </div>
                </>
              </AbacusControl>
              <AbacusVariant bucket={1}>{renderListingData()}</AbacusVariant>
              <AbacusVariant bucket={2}>{renderListingData(2)}</AbacusVariant>
            </AbacusExperiment>
          </UitkCardContent>
          <CardLink {...cardLinkProps} index={index} listingClickHandler={listingClickHandler} />
        </div>
        {!mapListingView && (
          <ListingFooter
            {...cardLinkProps}
            data-stid="listing-footer"
            listingFooter={listingFooter}
            listingClickHandler={listingClickHandler}
          />
        )}
        {!mapListingView &&
          alternateDates && (
            <AdditionalInfo {...alternateDatesInfo} displayHeaderText={true} keepOpen={true}>
              <AlternateDates data-stid="alternate-dates" {...alternateDatesProps} />
            </AdditionalInfo>
          )}
      </UitkCard>
    </li>
  );
};
